import { TimingEventAction, TimingEventType } from '@ikegps/core-api';
import { IkeDraw, Log } from '@ikegps/core-shared';
import type { CollectionModel } from '@ikegps/core-shared';
import { CurrentState } from '../state/page.state';
import { type IkeClientService, Services } from './index';
import { Logger } from './log.service';

export class DrawStateService implements IkeClientService {
	__priority = 100;

	_drawCacheKeys: string[] = [];
	_drawCacheMap: { [key: string]: IkeDraw } = {};

	constructor(protected log: Log) {}

	getIkeDraw(collection: CollectionModel, captureId: string): IkeDraw {
		if (CurrentState.CollectionEdit.collectionRevision === collection.revision) {
			return CurrentState.CollectionEdit.getIkeDraw(captureId);
		}

		const drawKey = `${collection.revision}:${captureId}`;
		const existing = this._drawCacheMap[drawKey];
		if (existing) {
			return existing;
		}

		const drawState = this.createIkeDraw(collection, captureId);
		this._drawCacheKeys.push(drawKey);
		this._drawCacheMap[drawKey] = drawState;

		// This draw caching limit is arbitrary, It will limit the number of captures
		// that can be rendered at one time in the report..
		if (this._drawCacheKeys.length > 1024) {
			const oldDraws = this._drawCacheKeys.splice(0, 10);
			for (const oldKey of oldDraws) {
				delete this._drawCacheMap[oldKey];
			}
		}
		return drawState;
	}

	createIkeDraw(collection: CollectionModel, captureId: string): IkeDraw {
		const capture = collection.state.getCapture(captureId);

		if (capture == null) {
			Services.Log.Logger.error({ collectionId: collection.id, captureId }, 'Missing capture');
			return null;
		}

		const window = Services.Window.window;
		return new IkeDraw({
			log: this.log,
			collection,
			capture,
			window,
			captureMaskFetcher: maskFetcher,
			onStartHOASession: () => {
				Services.Analytics.trackTiming(TimingEventType.HOA, TimingEventAction.ENTER, collection.id, new Date());
			},
		});
	}
}

const maskFetcher = async (collectionId: string, captureId: string) => {
	const preSignedMask = await Services.Department.rest.Capture.getMask({ collectionId, captureId });

	if (preSignedMask == null) {
		Logger.error({ collectionId, captureId }, 'Mask presigned URL was empty');
		return '';
	}

	const response = await fetch(preSignedMask.presignedUrl, { headers: { Accept: 'image/svg+xml' } });
	if (!response.ok) {
		Logger.error({ response, url: preSignedMask.presignedUrl }, 'Presigned URL returned bad response');
		return '';
	}

	Logger.debug({ collectionId, captureId, url: preSignedMask.presignedUrl }, 'Masks fetched');

	return await response.text();
};
